import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Edit3, Check, X, Loader2, AlertCircle } from 'lucide-react';

// Types
interface EditableFieldProps {
  value: string | number | undefined;
  onSave: (newValue: string | number) => Promise<void>;
  label?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'number' | 'textarea' | 'select' | 'date' | 'url';
  options?: Array<{ value: string; label: string }>;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  validation?: (value: string | number) => string | null; // Retourne null si valide, message d'erreur sinon
  icon?: ReactNode;
  multiline?: boolean;
  rows?: number;
  autoSave?: boolean; // Si true, sauvegarde automatiquement sur blur
  showEditButton?: boolean; // Si false, édition au clic
}

interface EditableFieldState {
  isEditing: boolean;
  isSaving: boolean;
  error: string | null;
  tempValue: string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value = '',
  onSave,
  label,
  placeholder = 'Cliquez pour éditer...',
  type = 'text',
  options = [],
  disabled = false,
  required = false,
  className,
  validation,
  icon,
  multiline: multilineProp = false,
  rows = 4,
  autoSave = false,
  showEditButton = true
}) => {
  // ✨ AMÉLIORATION UX: Activer automatiquement multiline quand type="textarea"
  const multiline = multilineProp || type === 'textarea';
  
  const [state, setState] = useState<EditableFieldState>({
    isEditing: false,
    isSaving: false,
    error: null,
    tempValue: String(value || '')
  });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  
  // ✨ ANTI-VIBRATION: Ref pour tracker la dernière valeur synchronisée
  const lastSyncedValueRef = useRef<string>(String(value || ''));

  // ✨ ANTI-VIBRATION: Synchroniser tempValue avec value quand value change
  // Seulement si la valeur a VRAIMENT changé (pas juste une référence)
  useEffect(() => {
    const newValue = String(value || '');
    if (!state.isEditing && lastSyncedValueRef.current !== newValue) {
      lastSyncedValueRef.current = newValue;
      setState(prev => ({ ...prev, tempValue: newValue }));
    }
  }, [value, state.isEditing]);

  // Focus sur l'input quand on entre en mode édition
  useEffect(() => {
    if (state.isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || 
          inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [state.isEditing]);

  const startEditing = () => {
    if (disabled) return;
    setState(prev => ({
      ...prev,
      isEditing: true,
      error: null,
      tempValue: String(value || '')
    }));
  };

  const cancelEditing = () => {
    setState(prev => ({
      ...prev,
      isEditing: false,
      error: null,
      tempValue: String(value || '')
    }));
  };

  const handleSave = async () => {
    // Validation
    if (validation) {
      const validationError = validation(type === 'number' ? Number(state.tempValue) : state.tempValue);
      if (validationError) {
        setState(prev => ({ ...prev, error: validationError }));
        return;
      }
    }

    // Vérification obligatoire
    if (required && !state.tempValue.trim()) {
      setState(prev => ({ ...prev, error: 'Ce champ est obligatoire' }));
      return;
    }

    // Si pas de changement, sortir du mode édition
    if (state.tempValue === String(value || '')) {
      cancelEditing();
      return;
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      const saveValue = type === 'number' ? Number(state.tempValue) : state.tempValue;
      await onSave(saveValue);
      setState(prev => ({
        ...prev,
        isEditing: false,
        isSaving: false,
        error: null
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la sauvegarde'
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    } else if (e.key === 'Enter' && e.ctrlKey && multiline) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleBlur = () => {
    if (autoSave && !state.isSaving) {
      handleSave();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setState(prev => ({ ...prev, tempValue: e.target.value, error: null }));
  };

  const displayValue = value || placeholder;
  const isEmpty = !value;

  // Mode édition
  if (state.isEditing) {
    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {type === 'select' ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={state.tempValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className={cn(
                'block w-full px-3 py-2 border-2 border-gray-300 rounded-lg shadow-sm',
                'focus:border-[#197ca8] focus:ring-2 focus:ring-[#197ca8]/20 text-sm',
                'transition-colors duration-200',
                state.error && 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
              )}
            >
              <option value="">Sélectionner...</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={state.tempValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              rows={rows}
              placeholder={placeholder}
              className={cn(
                'block w-full px-3 py-2 border-2 border-gray-300 rounded-lg shadow-sm',
                'focus:border-[#197ca8] focus:ring-2 focus:ring-[#197ca8]/20 text-sm',
                'transition-colors duration-200 resize-none',
                state.error && 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
              )}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type}
              value={state.tempValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={placeholder}
              className={cn(
                'block w-full px-3 py-2 border-2 border-gray-300 rounded-lg shadow-sm',
                'focus:border-[#197ca8] focus:ring-2 focus:ring-[#197ca8]/20 text-sm',
                'transition-colors duration-200',
                // Padding spécial pour les inputs date afin de ne pas cacher le bouton calendrier natif
                // Le calendrier natif a besoin d'environ 32px, les boutons prennent ~56px
                type === 'date' && 'pr-32',
                state.error && 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
              )}
            />
          )}
          
          {/* Boutons d'action */}
          {!autoSave && (
            <div 
              className={cn(
                "absolute top-1/2 transform -translate-y-1/2 flex items-center space-x-1 bg-white rounded px-1",
                // Positionner bien à gauche du bouton calendrier natif pour les inputs date
                type === 'date' ? 'right-14' : 'right-2'
              )}
              // z-index bas pour ne pas couvrir le picker de date natif
              style={type === 'date' ? { zIndex: 1 } : undefined}
            >
              <button
                type="button"
                onClick={handleSave}
                disabled={state.isSaving}
                className={cn(
                  'inline-flex items-center justify-center w-6 h-6 rounded',
                  'text-green-600 hover:bg-green-50 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {state.isSaving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                disabled={state.isSaving}
                className={cn(
                  'inline-flex items-center justify-center w-6 h-6 rounded',
                  'text-red-600 hover:bg-red-50 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* Erreur */}
        {state.error && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>{state.error}</span>
          </div>
        )}

        {/* Indications */}
        {!autoSave && (
          <div className="text-xs text-gray-500">
            {multiline ? 'Ctrl+Entrée pour sauvegarder' : 'Entrée pour sauvegarder'}, Échap pour annuler
          </div>
        )}
      </div>
    );
  }

  // Mode affichage
  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div
        onClick={showEditButton ? undefined : startEditing}
        className={cn(
          'group flex items-center space-x-2 min-h-[2.5rem] px-3 py-2',
          'border border-gray-300 rounded-lg bg-white',
          !showEditButton && !disabled && 'cursor-pointer hover:bg-gray-50 transition-colors',
          disabled && 'bg-gray-50'
        )}
      >
        {icon && (
          <span className="text-gray-400 flex-shrink-0">
            {icon}
          </span>
        )}
        
        <span className={cn(
          'flex-1 text-sm',
          isEmpty ? 'text-gray-400 italic' : 'text-gray-900',
          multiline ? 'whitespace-pre-wrap' : 'truncate'
        )}>
          {displayValue}
        </span>
        
        {showEditButton && !disabled && (
          <button
            type="button"
            onClick={startEditing}
            className={cn(
              'opacity-70 group-hover:opacity-100 transition-opacity',
              'inline-flex items-center justify-center w-6 h-6 rounded',
              'text-[#197ca8] hover:text-[#156a92] hover:bg-[#197ca8]/10'
            )}
            title="Cliquez pour modifier"
          >
            <Edit3 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// Composant pour les sections avec plusieurs champs éditables
interface EditableSectionProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

export const EditableSection: React.FC<EditableSectionProps> = ({
  title,
  icon,
  children,
  className
}) => {
  return (
    <div className={cn('bg-white rounded-lg border border-gray-200 shadow-sm', className)}>
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-2">
          {icon && (
            <span className="text-gray-500">
              {icon}
            </span>
          )}
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {children}
      </div>
    </div>
  );
};